import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, UploadCloud, X } from 'lucide-react'
import { toast } from 'sonner'
import { DetailDialog } from '@/shared/components/crud/detail-dialog'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import {
  analyseBankStatement,
  fetchBankStatementParsers,
  fetchManualUploadAccounts,
  uploadMultipleBankStatements,
} from '../api/bank-statements-api'
import { fileToBase64 } from '../lib/file-to-base64'
import { StatementAnalysisList } from './statement-analysis-list'
import type {
  BankStatementAnalysis,
  BankStatementUploadPayload,
} from '../types/bank-statement'

const ACCEPT = '.mt940,.txt,.xml,.ofx,.csv,.xlsx'
const FORM_ID = 'bank-statement-upload-form'
const TEMPLATE_URL = 'https://cdn.treasuryhub.ai/assets/th-bankstatements-custom-template.xlsx'

interface BankStatementUploadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, all analyses must map to this accountId (row-level invocation). */
  accountId?: number
}

export function BankStatementUploadDialog({
  open, onOpenChange, accountId,
}: BankStatementUploadDialogProps) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const [analyses, setAnalyses] = useState<BankStatementAnalysis[]>([])
  const [analysingCount, setAnalysingCount] = useState(0)
  const [dragging, setDragging] = useState(false)

  const { data: parsers = [] } = useQuery({
    queryKey: ['bank-statement-parsers'],
    queryFn: fetchBankStatementParsers,
    enabled: open,
    staleTime: 10 * 60 * 1000,
  })

  const { data: accounts = [] } = useQuery({
    queryKey: ['manual-upload-accounts'],
    queryFn: fetchManualUploadAccounts,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })

  function reset() {
    setFiles([])
    setAnalyses([])
    setAnalysingCount(0)
  }

  function handleOpenChange(v: boolean) {
    if (!v) reset()
    onOpenChange(v)
  }

  async function analyseFile(file: File) {
    setAnalysingCount((n) => n + 1)
    try {
      const result = await analyseBankStatement(file)
      setAnalyses((prev) => [...prev, ...result])
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error processing the file'
      toast.error(message)
      setFiles((prev) => prev.filter((f) => f !== file))
    } finally {
      setAnalysingCount((n) => n - 1)
    }
  }

  function addFiles(list: FileList | File[] | null) {
    if (!list) return
    const arr = Array.from(list)
    if (arr.length === 0) return
    setFiles((prev) => [...prev, ...arr])
    for (const file of arr) void analyseFile(file)
  }

  function removeFile(file: File) {
    setFiles((prev) => prev.filter((f) => f !== file))
    setAnalyses((prev) => prev.filter((a) => a.fileName !== file.name))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payloads: BankStatementUploadPayload[] = []
      for (const file of files) {
        const base64 = await fileToBase64(file)
        const segments = file.name.split('\\')
        const filename = segments[segments.length - 1] ?? file.name
        const matching = analyses.filter((a) => a.fileName === file.name)
        for (const a of matching) {
          payloads.push({
            Filename: filename,
            BankStatementParserId: a.parser.id,
            FileData64: base64,
            AccountId: a.accountId,
          })
        }
      }
      await uploadMultipleBankStatements(payloads)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['BankStatements'] })
      void qc.invalidateQueries({ queryKey: ['Accounts'] })
      toast.success('Bank statements uploaded')
      reset()
      onOpenChange(false)
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to upload statements'
      toast.error(message)
    },
  })

  const disabledUploadButton =
    files.length === 0 ||
    analysingCount > 0 ||
    analyses.length === 0 ||
    analyses.some((a) => !a.parser.id) ||
    analyses.some((a) => !a.accountId) ||
    analyses.some((a) => a.inErrorState) ||
    (accountId !== undefined && analyses.some((a) => a.accountId !== accountId))

  function updateAnalysis(index: number, patch: Partial<BankStatementAnalysis>) {
    setAnalyses((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  }

  return (
    <DetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Upload Bank Statement"
      formId={FORM_ID}
      isSubmitting={mutation.isPending}
      submitLabel="Upload"
      maxWidth="sm:max-w-3xl"
      footerLeft={
        <a
          href={TEMPLATE_URL}
          download
          className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
        >
          <Download className="h-4 w-4" /> Download template
        </a>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={(e) => { e.preventDefault(); if (!disabledUploadButton) mutation.mutate() }}
        className="flex flex-col gap-4"
      >
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload files"
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center transition-colors cursor-pointer',
            dragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          )}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            addFiles(e.dataTransfer.files)
          }}
        >
          <UploadCloud className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">Drop bank statements here or click to browse</p>
          <p className="text-xs text-muted-foreground">Accepted: {ACCEPT}</p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files)
              if (inputRef.current) inputRef.current.value = ''
            }}
          />
        </div>

        {files.length > 0 && (
          <ul className="flex flex-col gap-1">
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`} className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
                <span className="flex-1 truncate">{file.name}</span>
                <span className="text-xs text-muted-foreground shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <Button
                  type="button" variant="ghost" size="sm"
                  className="h-5 w-5 p-0 shrink-0"
                  onClick={() => removeFile(file)}
                  aria-label="Remove file"
                >
                  <X className="h-3 w-3" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {analysingCount > 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Analysing file…
          </div>
        )}

        {analyses.length > 0 && (
          <StatementAnalysisList
            analyses={analyses}
            parsers={parsers}
            accounts={accounts}
            accountIdLock={accountId}
            onParserChange={(i, parserId) => {
              const label = parsers.find((p) => p.Id === parserId)?.Name
              updateAnalysis(i, { parser: { id: parserId, label } })
            }}
            onAccountChange={(i, accId) => updateAnalysis(i, { accountId: accId })}
          />
        )}
      </form>
    </DetailDialog>
  )
}
